/**
 * Os dez cartões da tela inicial, na ordem em que a tela os desenha.
 *
 * Papel: `CARTOES_DA_TELA_INICIAL` é de onde `PaginaInicial` tira ícone, nome e
 * destino de cada cartão. A ordem aqui é a da tela — `financas, rh, veiculos,
 * residencias, compras, saude, documentos, mailing, orcamentos, usuarios` — e
 * não a de `CHAVES_MODULO`, que põe `orcamentos` antes de `compras` e
 * `mailing` antes de `saude`: são duas ordens para duas telas diferentes, a da
 * grade de permissões e a da tela inicial, e cada uma vive onde é lida.
 *
 * Como usar: a `chave` de cada cartão é tipada `ChaveModulo`, então uma grafia
 * divergente de `CHAVES_MODULO` vira erro de compilação em vez de um cartão
 * que silenciosamente nunca aparece para quem tem a permissão certa. Trocar a
 * posição de um cartão nesta lista move só a posição dele na grade — ela não
 * é lida por mais nada.
 *
 * O cartão de `usuarios` só aparece a quem administra: `acessoAlcanca` deixa
 * passar o administrador antes de olhar a lista, e a lista de mais ninguém tem
 * essa chave, porque ela fica fora de `MODULOS_CONCEDIVEIS` e o
 * `definirPermissoesSchema` recusa gravá-la. Ele deverá continuar sendo o
 * último: posto antes de outro, empurraria uma casa adiante, só na grade de
 * quem administra, todos os cartões que viessem depois dele. Cadastros-base não
 * tem cartão, e o único caminho até aquela tela é o link da barra do topo, no
 * `Layout`.
 *
 * Este arquivo não importa React nem monta nenhum elemento: os `Icone` são
 * referências aos componentes de `icones-de-modulo.tsx`, e é `CartaoDeModulo`
 * quem os instancia. Ficar em dado puro é o que permite `cartoes-da-tela-
 * inicial.test.ts` ler a lista sem montar uma árvore de componentes.
 */
import { type ChaveModulo } from '@concierge/shared';
import { type ComponentType, type SVGProps } from 'react';

import {
  IconeCompras,
  IconeDocumentos,
  IconeFinancas,
  IconeMailing,
  IconeOrcamentos,
  IconeResidencias,
  IconeRh,
  IconeSaude,
  IconeUsuarios,
  IconeVeiculos,
} from '@/shared/ui/icones-de-modulo';

/** Um cartão da tela inicial: chave, rota, ícone e quem libera vê-lo. */
export interface CartaoInicial {
  /** Chave do contrato, a mesma de `CHAVES_MODULO` e das permissões. */
  readonly chave: ChaveModulo;
  /** Caminho absoluto para onde o cartão navega, o mesmo declarado em `MODULOS`. */
  readonly rota: string;
  /** Componente do ícone, passado para `CartaoDeModulo` sem ser instanciado aqui. */
  readonly Icone: ComponentType<SVGProps<SVGSVGElement>>;
  /**
   * Chaves que, alcançando `ver`, liberam este cartão. Omitido, o cartão só é
   * liberado pela própria `chave` — é o caso de nove dos dez. Residências é o
   * único com mais de uma, porque é o único caminho até o Calendário e as
   * Manutenções da Fazenda Boa Vista, que não têm cartão próprio e vivem como
   * submenu dentro da tela de Residências.
   */
  readonly chavesQueLiberam?: readonly ChaveModulo[];
}

/**
 * Os dez cartões, na ordem em que aparecem na grade da tela inicial.
 *
 * `cartoes-da-tela-inicial.test.ts` confirma as dez chaves nesta ordem e que
 * toda `rota` existe em `MODULOS` — é o teste que protege esta lista de uma
 * edição distraída.
 */
export const CARTOES_DA_TELA_INICIAL: readonly CartaoInicial[] = [
  { chave: 'financas', rota: '/financas', Icone: IconeFinancas },
  { chave: 'rh', rota: '/rh', Icone: IconeRh },
  { chave: 'veiculos', rota: '/veiculos', Icone: IconeVeiculos },
  {
    chave: 'residencias',
    rota: '/residencias',
    Icone: IconeResidencias,
    chavesQueLiberam: ['residencias', 'calendario-boa-vista', 'manutencoes-boa-vista'],
  },
  { chave: 'compras', rota: '/compras', Icone: IconeCompras },
  { chave: 'saude', rota: '/saude', Icone: IconeSaude },
  { chave: 'documentos', rota: '/documentos', Icone: IconeDocumentos },
  { chave: 'mailing', rota: '/mailing', Icone: IconeMailing },
  { chave: 'orcamentos', rota: '/orcamentos', Icone: IconeOrcamentos },
  { chave: 'usuarios', rota: '/usuarios', Icone: IconeUsuarios },
] as const;

/**
 * As chaves que liberam um cartão ao alcançarem `ver`.
 *
 * @param cartao - Entrada de `CARTOES_DA_TELA_INICIAL`.
 * @returns `chavesQueLiberam`, quando declarado, ou um array só com a `chave`
 *   do próprio cartão.
 */
export function chavesQueLiberamCartao(cartao: CartaoInicial): readonly ChaveModulo[] {
  return cartao.chavesQueLiberam ?? [cartao.chave];
}
